import React, { useState, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Check, Loader2 } from 'lucide-react';
import { useReservationFlow } from './hooks/useReservationFlow';
import { useReservationSubmit } from './hooks/useReservationSubmit';
import { ReservationResult } from './types/reservation.types';
import ProgressBar from './components/ProgressBar';
import StepGuests from './steps/StepGuests';
import StepDate from './steps/StepDate';
import StepTime from './steps/StepTime';
import StepZone from './steps/StepZone';
import StepContact from './steps/StepContact';
import ReservationSuccess from './ReservationSuccess';
import WaitlistForm from './components/WaitlistForm';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';

interface ReservationFlowProps {
  restaurantId: string;
  onClose: () => void;
}

interface ZoneInfo {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  available: boolean;
}

const ReservationFlow: React.FC<ReservationFlowProps> = ({ restaurantId, onClose }) => {
  const flow = useReservationFlow();
  const { submit, submitting, error: submitError } = useReservationSubmit(restaurantId);
  const [result, setResult] = useState<ReservationResult | null>(null);
  const [tableSelectionMode, setTableSelectionMode] = useState<string>('none');
  const [showFloorPlan, setShowFloorPlan] = useState(false);
  const [zones, setZones] = useState<ZoneInfo[]>([]);
  const [zonesLoading, setZonesLoading] = useState(false);
  const [allSlotsFull, setAllSlotsFull] = useState(false);
  const [reservationDuration, setReservationDuration] = useState(90);
  const [maxGuests, setMaxGuests] = useState(10);

  // Whether zone step should be shown
  const shouldShowZoneStep = tableSelectionMode !== 'none' || showFloorPlan;

  // Fetch settings
  useEffect(() => {
    const fetchSettings = async () => {
      const { data } = await supabase
        .from('reservation_settings')
        .select('table_selection_mode, waitlist_enabled, show_floor_plan, reservation_duration_minutes, max_covers_per_reservation')
        .eq('restaurant_id', restaurantId)
        .maybeSingle();
      setTableSelectionMode(data?.table_selection_mode || 'none');
      setShowFloorPlan(data?.show_floor_plan ?? false);
      setReservationDuration(data?.reservation_duration_minutes ?? 90);
      setMaxGuests(data?.max_covers_per_reservation ?? 10);
    };
    fetchSettings();
  }, [restaurantId]);

  // Fetch zones when reaching zone step
  useEffect(() => {
    if (flow.currentStep !== 'zone' || !shouldShowZoneStep) return;

    const fetchZones = async () => {
      setZonesLoading(true);
      try {
        // Get the active floor plan to find zones
        const { data: floorPlan } = await supabase
          .from('floor_plans')
          .select('id')
          .eq('restaurant_id', restaurantId)
          .eq('is_active', true)
          .order('created_at', { ascending: false })
          .limit(1)
          .maybeSingle();

        if (!floorPlan) {
          flow.next();
          return;
        }

        const { data: fpZones } = await supabase
          .from('floor_plan_zones')
          .select('*')
          .eq('floor_plan_id', floorPlan.id)
          .eq('is_bookable', true)
          .order('sort_order');

        const bookableZones = (fpZones || []);
        if (bookableZones.length === 0) {
          flow.next();
          return;
        }

        // Fetch zone capacities to check actual availability
        const { data: resSettings } = await supabase
          .from('reservation_settings')
          .select('zone_capacities, capacity_mode')
          .eq('restaurant_id', restaurantId)
          .maybeSingle();

        const zoneCapacities = (resSettings?.zone_capacities as Record<string, { tables: number; covers: number }>) ?? {};
        const capacityMode = resSettings?.capacity_mode ?? 'global';

        setZones(bookableZones.map((z: any) => {
          let zoneAvailable = true;
          if (capacityMode === 'per_zone') {
            const zc = zoneCapacities[z.id];
            zoneAvailable = !!(zc && zc.covers > 0);
          }
          return {
            id: z.id,
            name: z.name,
            description: z.description,
            icon: z.icon,
            available: zoneAvailable,
          };
        }));
      } catch {
        flow.next();
      } finally {
        setZonesLoading(false);
      }
    };
    fetchZones();
  }, [flow.currentStep, shouldShowZoneStep, restaurantId]);

  const handleSubmit = async () => {
    const res = await submit(flow.data);
    if (res) {
      setResult(res);
      flow.goToSuccess();
    }
  };

  const handleClose = () => {
    flow.reset();
    onClose();
  };

  // Handle step skipping for zone
  const handleNext = () => {
    if (flow.currentStep === 'time' && !shouldShowZoneStep) {
      flow.skipZone();
    } else {
      flow.next();
    }
  };

  // Auto-skip zone step when going back if zone is not needed
  useEffect(() => {
    if (flow.currentStep === 'zone' && !shouldShowZoneStep) {
      flow.back(); // will go to 'time'
    }
  }, [flow.currentStep, shouldShowZoneStep]);

  if (flow.currentStep === 'success' && result) {
    return <ReservationSuccess result={result} onClose={handleClose} />;
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Header */}
      <div className="flex-shrink-0">
        <div className="flex items-center px-3 pt-2">
          {flow.stepIndex > 0 && (
            <Button variant="ghost" size="icon" onClick={flow.back} className="h-9 w-9">
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <div className="flex-1" />
        </div>
        <ProgressBar currentStep={flow.stepIndex} totalSteps={shouldShowZoneStep ? 5 : 4} />
      </div>

      {/* Step content */}
      <div className="flex-1 overflow-y-auto px-4">
        <AnimatePresence mode="wait">
          {flow.currentStep === 'guests' && (
            <StepGuests
              key="guests"
              value={flow.data.partySize}
              maxGuests={maxGuests}
              onSelect={(n) => flow.updateData({ partySize: n })}
              onAutoAdvance={flow.next}
            />
          )}
          {flow.currentStep === 'date' && (
            <StepDate
              key="date"
              restaurantId={restaurantId}
              value={flow.data.date}
              onSelect={(d) => flow.updateData({ date: d })}
              onAutoAdvance={flow.next}
            />
          )}
          {flow.currentStep === 'time' && flow.data.date && flow.data.partySize && (
            <StepTime
              key="time"
              restaurantId={restaurantId}
              date={flow.data.date}
              partySize={flow.data.partySize}
              selectedTime={flow.data.timeSlot}
              onSelect={(t) => flow.updateData({ timeSlot: t })}
              onAutoAdvance={handleNext}
              onAllSlotsFull={setAllSlotsFull}
            />
          )}
          {flow.currentStep === 'zone' && (
            <StepZone
              key="zone"
              zones={zones}
              loading={zonesLoading}
              selectedZone={flow.data.zonePreference || null}
              onSelect={(zoneId) => flow.updateData({ zonePreference: zoneId })}
              onAutoAdvance={flow.next}
            />
          )}
          {flow.currentStep === 'contact' && (
            <StepContact
              key="contact"
              data={flow.data}
              onUpdate={flow.updateData}
            />
          )}
        </AnimatePresence>

        {/* Waitlist form when all slots full */}
        {flow.currentStep === 'time' && allSlotsFull && flow.data.date && flow.data.partySize && (
          <WaitlistForm
            restaurantId={restaurantId}
            date={flow.data.date}
            partySize={flow.data.partySize}
            desiredTime={flow.data.timeSlot}
            onBack={() => flow.back()}
          />
        )}
      </div>

      {/* Footer - submit button for contact step */}
      {flow.currentStep === 'contact' && (
        <div className="flex-shrink-0 p-4 border-t">
          {submitError && (
            <p className="text-sm text-destructive text-center mb-2">{submitError}</p>
          )}
          <Button
            onClick={handleSubmit}
            disabled={!flow.canProceed || submitting}
            className="w-full h-12 text-base font-semibold gap-2"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Réservation en cours…
              </>
            ) : (
              <>
                <Check className="h-4 w-4" />
                Confirmer ma réservation
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );
};

export default ReservationFlow;
